import { getIllustrationUrl } from "@/utils/links";
import { Stack, Typography } from "@mui/material";

export enum API_NAMES {
  CREATE_PROJECT,
  CREATE_JOB,
  CREATE_JOB_WITH_PROJECT,
  CHECK_JOB_STATUS
}

const YOUR_API_KEY = "YOUR_API_KEY";
const PROJECT_ID = "PROJECT_ID";

const API_ENDPOINT = import.meta.env.VITE_MURF_API_ENDPOINT;

export const API_DOCS_SECTION = {
  HEADER: "HEADER",
  OVERVIEW: "OVERVIEW",
  QUICKSTART: "QUICKSTART",
  LANGUAGES: "LANGUAGES",
  API_LIMITS: "API_LIMITS",
  FAQ: "FAQ"
};

type ApiLinkType = API_NAMES | "TROUBLESHOOT";

export const handleApiRefRedirect = (linkType?: ApiLinkType) => {
  const API_REF_URL = "https://murf.ai/api/docs";
  let path = "";
  switch (linkType) {
    case API_NAMES.CREATE_JOB:
      path = "/api-reference/dubbing/jobs/create";
      break;
    case API_NAMES.CHECK_JOB_STATUS:
      path = "/api-reference/dubbing/jobs/get-status";
      break;
    case API_NAMES.CREATE_JOB_WITH_PROJECT:
      path = "/api-reference/dubbing/jobs/create-with-project-id";
      break;
    case API_NAMES.CREATE_PROJECT:
      path = "/api-reference/dubbing/projects/create";
      break;
    case "TROUBLESHOOT":
      path = "/capabilities/dubbing#troubleshooting";
      break;
    default:
      path = "/";
      break;
  }
  window.open(API_REF_URL + path, "_blank");
};

export const API_DOCS_DATA = {
  [API_DOCS_SECTION.HEADER]: {
    title: "Murf Dub Automation API",
    description:
      "Automate high-quality dubbing with realistic, multi-language support in bulk. Scale dubbing for apps, games, and videos in minutes. Integrate the API in your workflow seamlessly—globalizing your content faster than ever.",
    childElements: [
      {
        title: "Dub fast in bulk",
        description: "Upload and dub thousands of videos with rapid turnaround",
        img: getIllustrationUrl({
          illustrationNameWithExtension: "dub-api-1.svg"
        })
      },
      {
        title: "Automate dubbing workflows",
        description:
          "Streamline your dubbing process with automation, saving time and ensuring consistency across projects.",
        img: getIllustrationUrl({
          illustrationNameWithExtension: "dub-api-2.svg"
        })
      },
      {
        title: "Enterprise grade security",
        description:
          "Strict data handling policies, option for ephemeral storage and controlled retention periods",
        img: getIllustrationUrl({
          illustrationNameWithExtension: "dub-api-3.svg"
        })
      }
    ]
  },
  [API_DOCS_SECTION.OVERVIEW]: {
    title: "Overview",
    description:
      "The Murf Dub Automation API can be used in two ways, depending on your needs.",
    childElements: [
      {
        title: "Transient (Temporary & Lightweight)",
        description:
          "Generate dubs instantly with temporary storage—output links expire in 72 hours, ensuring secure, API-only processing without storing data on the Murf platform."
      },
      {
        title: "Persistent (Project-Based & Editable)",
        description:
          "Create and manage dubs via API or the Murf Dubbing platform UI, enabling editing, re-synthesis, QA checks, collaboration, refinements, and re-downloads if needed."
      }
    ]
  },
  [API_DOCS_SECTION.QUICKSTART]: {
    title: "Quickstart",
    description:
      "To automate dubbing of your files and keep the process API-based only, Create a job is recommended. These dubs expire after 72 hours and cannot be edited through UI."
  },
  [API_DOCS_SECTION.LANGUAGES]: {
    title: "Languages",
    description:
      "Here are all the source and destination languages offered by Murf Dub Automation API in both types of dubbing:"
  },
  [API_DOCS_SECTION.API_LIMITS]: {
    title: "API Limits",
    description:
      "API usage and rate limits are determined by your pricing plan. If you need higher limits, you can upgrade to a higher-tier plan.",
    childElements: [["Plan", "Free", "Pay-as-you-go", "Enterprise"]]
  }
};

export const API_DOC_FAQ: {
  question: string | { type: string; text: string }[];
  answer: (React.ReactNode | string)[];
}[] = [
  {
    question: "How do I bulk upload my entire library?",
    answer: [
      "You can programmatically loop through your file list and call the relevant job-creation endpoint for each file. This can be done by giving a public link to the video/audio or uploading it locally through multipart form-data(recommended for security).",
      "For large-scale operations, you may batch your requests to manage concurrency or network throughput efficiently."
    ]
  },
  {
    question:
      "What service-level agreements (SLAs) apply to the Bulk Dubbing API?",
    answer: [
      "We typically offer 99% uptime. For premium-tier customers, higher availability SLAs may be negotiated.",
      "Processing speed depends on file length, concurrency levels, and subscription tier. Advanced or premium plans may grant higher throughput or priority in the dubbing queue."
    ]
  },
  {
    question: "How do I pay for the dubbing services?",
    answer: [
      "Credits Model: Each dubbing consumes credits based on the minutes and number of languages.",
      "Usage Dashboard: Track how many credits you’ve used, how many remain, and get alerts when approaching limits."
    ]
  },
  {
    question: "What happens if a dub fails?",
    answer: [
      "You’ll receive an HTTP error code and/or a webhook payload indicating the failure reason (e.g., insufficient credits, unsupported file format, file corruption).",
      "For project-based jobs, you can see the failure reason in the platform. For ephemeral jobs, you’ll only see it via the API response or webhook.",
      <Stack direction={"row"} component={"li"} gap={0.5} mt={0.2}>
        <Typography variant="body1">For a full list, see </Typography>
        <Typography
          variant="subtitle1"
          alignItems={"end"}
          color="primary.light"
          sx={{
            textDecoration: "underline",
            cursor: "pointer"
          }}
          onClick={() => handleApiRefRedirect("TROUBLESHOOT")}
        >
          API Reference
        </Typography>
      </Stack>
    ]
  },
  {
    question: "Can I edit an uploaded dub?",
    answer: [
      "Yes, for project-based jobs: The Murf platform enables script, timing, or voice changes. You can finalize the result and re-download.",
      "No, for ephemeral jobs: There is no persistent record in the platform; the link is deleted after 72 hours."
    ]
  },
  {
    question: "Is the API usage gated by my price plan?",
    answer: [
      "Yes: Different plans have varying concurrency limits and advanced features (such as QA checks). The differences between the plans are highlighted in the section above.",
      "You can contact our sales team or view your plan details in the platform to see upgrade options."
    ]
  }
];

export type PROGRAMMING_LANGUAGE_TYPE = {
  language: string;
  title: string;
  img: string;
};

export enum CODE_LANGUAGE_NAME {
  BASH = "bash",
  PYTHON = "python",
  PYTHON_SDK = "python sdk",
  JAVASCRIPT = "javascript",
  PHP = "php",
  RUBY = "ruby",
  CSHARP = "csharp",
  GO = "go"
}

export const CODE_LANGUAGE_DATA: PROGRAMMING_LANGUAGE_TYPE[] = [
  {
    language: CODE_LANGUAGE_NAME.BASH,
    title: "Curl",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.PYTHON,
    title: "Python",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.PYTHON_SDK,
    title: "Python (SDK)",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.JAVASCRIPT,
    title: "Node JS",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.PHP,
    title: "PHP",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.RUBY,
    title: "Ruby",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/ruby/ruby-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.CSHARP,
    title: "C#",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/csharp/csharp-original.svg"
  },
  {
    language: CODE_LANGUAGE_NAME.GO,
    title: "Go",
    img: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/go/go-original.svg"
  }
];

export const getCodeSnippets = {
  [API_NAMES.CREATE_PROJECT]: (
    language: CODE_LANGUAGE_NAME,
    {
      qcEnabled,
      targetLocales,
      sourceLocale,
      projectName
    }: {
      qcEnabled: boolean;
      targetLocales: string[];
      sourceLocale: string;
      projectName: string;
    }
  ): string => {
    const URL = `${API_ENDPOINT}/v1/murfdub/projects/create`;
    const dubbingType = qcEnabled ? "QA" : "AUTOMATED";

    switch (language) {
      case CODE_LANGUAGE_NAME.BASH:
        return `
curl -X POST ${URL} 
     -H 'api-key: ${YOUR_API_KEY}' 
     -H 'Content-Type: application/json' 
     -d '{
  "name": "${projectName}",${
          sourceLocale && sourceLocale !== "auto"
            ? `
  "source_locale": "${sourceLocale}",`
            : ""
        }
  "dubbing_type": "${dubbingType}",
  "target_locales": ${targetLocales ? JSON.stringify(targetLocales) : "[]"}
}'
     `;
      case CODE_LANGUAGE_NAME.CSHARP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.GO:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.JAVASCRIPT:
        return `
const axios = require("axios");

async function createProject() {
    const url = '${URL}';
    const options = {
        headers: { 'api-key': "${YOUR_API_KEY}", 'Content-Type': 'application/json' },
    };
    const body = {
        "name": "${projectName}",
        "dubbing_type": "${dubbingType}",${
          sourceLocale && sourceLocale !== "auto"
            ? `
        "source_locale": "${sourceLocale}",`
            : ""
        }
        "target_locales": ${
          targetLocales ? JSON.stringify(targetLocales) : "[]"
        }
    }

    try {
        const response = await axios.post(url, body, options);
        const data = await response.data;
        console.log(data);
    } catch (error) {
        console.error(error);
    }
}

createProject();
        `;
      case CODE_LANGUAGE_NAME.PHP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.PYTHON:
        return `
import requests

url = "${URL}"

payload = {
    "name": "${projectName}",
    "dubbing_type": "${dubbingType}",${
          sourceLocale && sourceLocale !== "auto"
            ? `
    "source_locale": "${sourceLocale}",`
            : ""
        }
    "target_locales":${targetLocales ? JSON.stringify(targetLocales) : "[]"}
}
headers = {
    "api-key": "${YOUR_API_KEY}",
    "Content-Type": "application/json"
}

response = requests.post(url, json=payload, headers=headers)

print(response.json())
        `;
      case CODE_LANGUAGE_NAME.PYTHON_SDK:
        return `
from murf import MurfDub

client = MurfDub(
    api_key="${YOUR_API_KEY}", # Not required if you have set the environment variable MURFDUB_API_KEY
)
create_response = client.dubbing.projects.create(
    name="${projectName}",
    dubbing_type="${dubbingType}",${
          sourceLocale && sourceLocale !== "auto"
            ? `
    source_locale= "${sourceLocale}",`
            : ""
        }
    target_locales=${
      targetLocales ? JSON.stringify(targetLocales) : "[]"
    }, # Specify the languages you want to dub in
)

print("Project created successfully:", create_response)

        `;
      case CODE_LANGUAGE_NAME.RUBY:
        return `
@TODO;
        `;
      default:
        return "";
    }
  },
  [API_NAMES.CREATE_JOB]: (
    language: CODE_LANGUAGE_NAME,
    {
      sourceLocale,
      targetLocales
    }: {
      sourceLocale: string;
      targetLocales: string[];
    }
  ) => {
    const URL = `${API_ENDPOINT}/v1/murfdub/jobs/create`;

    switch (language) {
      case CODE_LANGUAGE_NAME.BASH:
        return `
curl -X POST ${URL}
     -H 'api-key: ${YOUR_API_KEY}'
     -H 'Content-Type: multipart/form-data'
     -F 'file=@<file1>'${
       sourceLocale && sourceLocale !== "auto"
         ? `
     -F 'source_locale="${sourceLocale ?? ""}"'`
         : ""
     }${
          targetLocales
            ? targetLocales
                .map(
                  (l) => `
     -F 'target_locales="${l}"'`
                )
                .join("")
            : ""
        }
        `;
      case CODE_LANGUAGE_NAME.CSHARP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.GO:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.JAVASCRIPT:
        return `
const fs = require("fs");
const FormData = require("form-data");
const axios = require("axios");

async function createJob() {
    const url = "${URL}";
    const form = new FormData();
    const fileStream = fs.createReadStream( path_to_your_file );
    form.append("file", fileStream);
    ${
      sourceLocale && sourceLocale !== "auto"
        ? `form.append("source_locale", "${sourceLocale ?? ""}");
    `
        : ""
    }${
          targetLocales
            ? targetLocales
                .map(
                  (l) => `form.append("target_locales", "${l}");
    `
                )
                .join("")
            : ""
        }
    const options = {
        headers: { 
          ...form.getHeaders(),
          "api-key": ${YOUR_API_KEY}
        },
    };

    options.body = form;

    try {
        const response = await axios.post(url, form, options)
        const data = response.data;
        console.log("data", data);
    } catch (error) {
        console.error("error", error.response.data);
    }
}

createJob();

`;
      case CODE_LANGUAGE_NAME.PHP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.PYTHON:
        return `
import requests

url = "${URL}"

payload = {${
          sourceLocale && sourceLocale !== "auto"
            ? `
    "source_locale" : "${sourceLocale}",`
            : ""
        }
    "target_locales":[${
      targetLocales ? targetLocales.map((l) => `"${l}"`).join(",") : ""
    }],
}

files = {
    "file": open('path_to_your_file', 'rb')
}
headers = {"api-key": "${YOUR_API_KEY}"}

response = requests.post(url, data=payload, files=files, headers=headers)

print(response.json())
        `;
      case CODE_LANGUAGE_NAME.PYTHON_SDK:
        return `
from murf import MurfDub

client = MurfDub(
    api_key="${YOUR_API_KEY}", # Not required if you have set the environment variable MURFDUB_API_KEY
)

file_path = "PATH_TO_YOUR_FILE" # Path to the file you want to dub

create_response = client.dubbing.jobs.create(${
          sourceLocale && sourceLocale !== "auto"
            ? `
    source_locale = "${sourceLocale}",`
            : ""
        }
    target_locales=${
      targetLocales ? JSON.stringify(targetLocales) : "[]"
    }, # Specify the languages you want to dub in
    file_name="File Name", # Name of the file for your reference
    file=open(file_path, "rb"),
    priority="LOW", # Optional: Set the priority of the job (LOW, NORMAL, HIGH)
    # webhook_url="WEBHOOK_URL", # Optional: URL to receive webhook notifications on job status changes
)

print("Job created successfully:", create_response)

        `;
      case CODE_LANGUAGE_NAME.RUBY:
        return `
@TODO;
        `;
      default:
        return "";
    }
  },
  [API_NAMES.CREATE_JOB_WITH_PROJECT]: (
    language: CODE_LANGUAGE_NAME,
    { projectId }: { projectId: string }
  ) => {
    const URL = `${API_ENDPOINT}/v1/murfdub/jobs/create-with-project-id`;

    switch (language) {
      case CODE_LANGUAGE_NAME.BASH:
        return `
curl -X POST ${URL}
     -H 'api-key: ${YOUR_API_KEY}'
     -H 'Content-Type: multipart/form-data'
     -F 'file=@<file1>'
     -F 'project_id="${projectId ? projectId : PROJECT_ID}"'
        `;
      case CODE_LANGUAGE_NAME.CSHARP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.GO:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.JAVASCRIPT:
        return `
const fs = require("fs");
const FormData = require("form-data");
const axios = require("axios");

async function createJobWithProject(projectId) {
    const url = '${URL}';
    const form = new FormData();
    form.append('file', fs.createReadStream(path_to_your_file));
    form.append('project_id', projectId);

    const options = { headers: { ...form.getHeaders(), 'api-key': ${YOUR_API_KEY} } };

    options.body = form;

    try {
        const response = await axios.post(url, form, options);
        const data = await response.data;
        console.log(data);
    } catch (error) {
        console.error(error.response.data);
    }
}

createJobWithProject(${projectId ? `"${projectId}"` : PROJECT_ID})

        `;
      case CODE_LANGUAGE_NAME.PHP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.PYTHON:
        return `
import requests

url = "${URL}"

payload = {
    "project_id": "${projectId ? projectId : PROJECT_ID}",
}

files = {
    "file": open('path_to_your_file', 'rb')
}
headers = {"api-key": "${YOUR_API_KEY}"}

response = requests.post(url, data=payload, files=files, headers=headers)

print(response.json())
        `;
      case CODE_LANGUAGE_NAME.PYTHON_SDK:
        return `
from murf import MurfDub

client = MurfDub(
    api_key="${YOUR_API_KEY}", # Not required if you have set the environment variable MURFDUB_API_KEY
)

file_path = "PATH_TO_YOUR_FILE" # Path to the file you want to dub

create_response = client.dubbing.jobs.create_with_project_id(
    file_name="PeppaGoesToLondon", # Name of the file for your reference
    file=open(file_path, "rb"),
    project_id="${
      projectId || PROJECT_ID
    }", # ID of the project that you want to associate the job with
    # webhook_url="WEBHOOK_URL", # Optional: URL to receive webhook notifications on job status changes
)

print("Job created successfully:", create_response)

        `;
      case CODE_LANGUAGE_NAME.RUBY:
        return `
@TODO;
        `;
      default:
        return "";
    }
  },
  [API_NAMES.CHECK_JOB_STATUS]: (language: CODE_LANGUAGE_NAME) => {
    const URL = `${API_ENDPOINT}/v1/murfdub/jobs/JOB_ID/status`;

    switch (language) {
      case CODE_LANGUAGE_NAME.BASH:
        return `
curl ${URL}
     -H "api-key: ${YOUR_API_KEY}"
        `;
      case CODE_LANGUAGE_NAME.CSHARP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.GO:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.JAVASCRIPT:
        return `
const axios = require("axios");

async function checkJobStatus(jobId) {
    const url = '${API_ENDPOINT}/v1/murfdub/jobs/' + jobId + '/status';

    const options = {
        headers: { "api-key": "${YOUR_API_KEY}" },
    };

    try {
        const response = await axios.get(url, options)
        const data = await response.data;
        console.log(data);
    } catch (error) {
        console.error(error);
    }
}

checkJobStatus(jobId);

        `;
      case CODE_LANGUAGE_NAME.PHP:
        return `
@TODO;
        `;
      case CODE_LANGUAGE_NAME.PYTHON:
        return `
import requests


JOB_ID = "JOB_ID"
url = f"${API_ENDPOINT}/v1/murfdub/jobs/{JOB_ID}/status"

headers = {"api-key": "${YOUR_API_KEY}"}

response = requests.get(url, headers=headers)

print(response.json())
        `;
      case CODE_LANGUAGE_NAME.PYTHON_SDK:
        return `
from murf import MurfDub

client = MurfDub(
    api_key="${YOUR_API_KEY}", # Not required if you have set the environment variable MURFDUB_API_KEY
)
status_res = client.dubbing.jobs.get_status(
    job_id="job_id",
)
print("Job status:", status_res)

        `;
      case CODE_LANGUAGE_NAME.RUBY:
        return `
@TODO;
        `;
      default:
        return "";
    }
  }
};
